import React, { useState, useEffect, useCallback } from 'react';
import { MediaFileRead, MediaFileUploadResponse, MediaFileListResponse } from '../types/api';
import { apiService } from '../services/api';
import { MediaCard } from './MediaCard';
import { MediaUpload } from './MediaUpload';
import { Upload, Grid, List, Search, Filter } from 'lucide-react';

export const MediaManager: React.FC = () => {
  const [mediaFiles, setMediaFiles] = useState<MediaFileRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showUpload, setShowUpload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  
  const pageSize = 20;

  const loadMediaFiles = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError(undefined);
    
    try {
      const response: MediaFileListResponse = await apiService.listMediaFiles(page, pageSize);
      
      if (page === 1) {
        setMediaFiles(response.files);
      } else {
        setMediaFiles(prev => [...prev, ...response.files]);
      }
      
      setTotalCount(response.total_count);
      setCurrentPage(response.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media files');
      console.error('Failed to load media files:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMediaFiles(1);
  }, [loadMediaFiles]);

  const handleUploadComplete = (uploadedFile: MediaFileUploadResponse) => {
    // Convert upload response to MediaFileRead format
    const newMediaFile: MediaFileRead = {
      id: uploadedFile.id,
      user_id: '', // This will be filled by the backend
      original_filename: uploadedFile.original_filename,
      ui_filename: uploadedFile.ui_filename,
      content_type: uploadedFile.content_type,
      file_size: uploadedFile.file_size,
      notes: uploadedFile.notes,
      upload_date: uploadedFile.upload_date,
      is_active: true
    };
    
    setMediaFiles(prev => [newMediaFile, ...prev]);
    setTotalCount(prev => prev + 1);
    setShowUpload(false);
  };

  const handleMediaUpdate = (updatedMedia: MediaFileRead) => {
    setMediaFiles(prev => 
      prev.map(media => 
        media.id === updatedMedia.id ? updatedMedia : media
      )
    );
  };

  const handleMediaDelete = (mediaId: string) => {
    setMediaFiles(prev => prev.filter(media => media.id !== mediaId));
    setTotalCount(prev => prev - 1);
  };

  const handleLoadMore = () => {
    if (!loading && mediaFiles.length < totalCount) {
      loadMediaFiles(currentPage + 1);
    }
  };

  const filteredAndSortedMedia = React.useMemo(() => {
    let filtered = mediaFiles;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(media =>
        media.ui_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        media.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (media.notes && media.notes.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.ui_filename.localeCompare(b.ui_filename);
        case 'size':
          return b.file_size - a.file_size;
        case 'date':
        default:
          return new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime();
      }
    });
    
    return filtered;
  }, [mediaFiles, searchTerm, sortBy]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalSize = () => {
    const totalBytes = mediaFiles.reduce((sum, media) => sum + media.file_size, 0);
    return formatFileSize(totalBytes);
  };

  if (showUpload) {
    return (
      <div className="max-w-2xl mx-auto">
        <MediaUpload
          onUploadComplete={handleUploadComplete}
          onCancel={() => setShowUpload(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="text-sm text-gray-600 mt-1">
            {totalCount} files • {getTotalSize()} total
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Upload size={16} className="mr-2" />
          Upload Media
        </button>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files and notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sort */}
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
              className="border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
          </div>

          {/* View Mode */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${
                viewMode === 'grid' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${
                viewMode === 'list' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => loadMediaFiles(1)}
            className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Try again
          </button>
        </div>
      )}

      {/* Content */}
      {filteredAndSortedMedia.length === 0 && !loading ? (
        <div className="text-center py-12">
          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No media files</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'No files match your search.' : 'Upload your first media file to get started.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Upload size={16} className="mr-2" />
              Upload Media
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Media Grid/List */}
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }>
            {filteredAndSortedMedia.map((media) => (
              <MediaCard
                key={media.id}
                media={media}
                onUpdate={handleMediaUpdate}
                onDelete={handleMediaDelete}
              />
            ))}
          </div>

          {/* Load More */}
          {mediaFiles.length < totalCount && (
            <div className="text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Loading State */}
      {loading && mediaFiles.length === 0 && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading your media files...</p>
        </div>
      )}
    </div>
  );
};
